import React, { useRef, useEffect, useState } from 'react';

// ============================================================
// RichTextEditor – Editor WYSIWYG con contentEditable
// Salida: HTML string. Límite configurable en bytes (def. 65535)
// ============================================================

interface Props {
  value:        string;
  onChange:     (html: string) => void;
  placeholder?: string;
  maxBytes?:    number;
  error?:       string;
}

// ── Paleta de colores de texto ─────────────────────────────────
const TEXT_COLORS = [
  { label: 'Negro',    value: '#1e293b' },
  { label: 'Rojo',     value: '#dc2626' },
  { label: 'Naranja',  value: '#ea580c' },
  { label: 'Amarillo', value: '#ca8a04' },
  { label: 'Verde',    value: '#16a34a' },
  { label: 'Azul',     value: '#2563eb' },
  { label: 'Morado',   value: '#7c3aed' },
  { label: 'Rosa',     value: '#db2777' },
  { label: 'Cian',     value: '#0891b2' },
  { label: 'Gris',     value: '#64748b' },
];

// ── Paleta de resaltado ────────────────────────────────────────
const HL_COLORS = [
  { label: 'Amarillo',  value: '#fef08a' },
  { label: 'Verde',     value: '#bbf7d0' },
  { label: 'Azul',      value: '#bae6fd' },
  { label: 'Rosa',      value: '#fbcfe8' },
  { label: 'Naranja',   value: '#fed7aa' },
  { label: 'Morado',    value: '#e9d5ff' },
  { label: 'Rojo',      value: '#fecaca' },
  { label: 'Sin color', value: 'transparent' },
];

// ── Fuentes ────────────────────────────────────────────────────
const FONTS = [
  { label: 'Predeterminada', value: 'inherit'   },
  { label: 'Arial',          value: 'Arial'      },
  { label: 'Georgia',        value: 'Georgia'    },
  { label: 'Courier New',    value: 'Courier New'},
  { label: 'Verdana',        value: 'Verdana'    },
  { label: 'Times New Roman',value: 'Times New Roman' },
];

// ── Tamaños ────────────────────────────────────────────────────
const SIZES = [
  { label: 'Pequeño',    value: '1' },
  { label: 'Normal',     value: '3' },
  { label: 'Grande',     value: '4' },
  { label: 'Muy grande', value: '5' },
  { label: 'Título',     value: '6' },
];

// ── Botón de toolbar ───────────────────────────────────────────
const ToolBtn: React.FC<{
  title: string; onMouseDown: (e: React.MouseEvent) => void;
  children: React.ReactNode; active?: boolean;
}> = ({ title, onMouseDown, children, active }) => (
  <button type="button" title={title} onMouseDown={onMouseDown}
    className={`p-1.5 rounded-lg transition-colors flex-shrink-0
      ${active ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-700'}`}>
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-slate-200 mx-0.5 flex-shrink-0 self-center"/>;

// ── Dropdown genérico ──────────────────────────────────────────
type DropdownKey = 'color' | 'highlight' | 'table' | null;

// ── Componente principal ───────────────────────────────────────
const RichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder = 'Escribe el contenido…',
  maxBytes    = 65535,
  error,
}) => {
  const editorRef               = useRef<HTMLDivElement>(null);
  const [open, setOpen]         = useState<DropdownKey>(null);
  const [tRows, setTRows]       = useState('3');
  const [tCols, setTCols]       = useState('3');

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byteLen   = new TextEncoder().encode(value).length;
  const pct       = Math.min((byteLen / maxBytes) * 100, 100);
  const overLimit = byteLen > maxBytes;

  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const toggle = (key: DropdownKey) =>
    setOpen(prev => (prev === key ? null : key));

  const close = () => setOpen(null);

  const handleInput = () => onChange(editorRef.current?.innerHTML ?? '');

  const applyFont = (e: React.ChangeEvent<HTMLSelectElement>) => {
    editorRef.current?.focus();
    document.execCommand('fontName', false, e.target.value);
  };

  const applySize = (e: React.ChangeEvent<HTMLSelectElement>) => {
    editorRef.current?.focus();
    document.execCommand('fontSize', false, e.target.value);
  };

  const insertTable = () => {
    const r = Math.max(1, Math.min(20, Number(tRows) || 3));
    const c = Math.max(1, Math.min(10, Number(tCols) || 3));
    const header = Array(c).fill(0).map(() =>
      `<th style="border:1px solid #94a3b8;padding:6px 10px;background:#f1f5f9;font-weight:600;text-align:left;">Encabezado</th>`
    ).join('');
    const body = Array(Math.max(0, r - 1)).fill(0).map(() =>
      `<tr>${Array(c).fill(0).map(() =>
        `<td style="border:1px solid #cbd5e1;padding:6px 10px;min-width:60px;">&nbsp;</td>`
      ).join('')}</tr>`
    ).join('');
    exec('insertHTML',
      `<table style="border-collapse:collapse;width:100%;margin:8px 0">` +
        `<tbody><tr>${header}</tr>${body}</tbody>` +
      `</table><p><br></p>`
    );
    close();
  };

  return (
    <div>
      <div className={`rounded-xl border overflow-hidden ${error ? 'border-red-300' : 'border-slate-200'}`}>

        {/* ═══ BARRA DE HERRAMIENTAS ═══ */}
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50">

          {/* Fuente */}
          <select
            title="Fuente"
            onChange={applyFont}
            onMouseDown={e => e.stopPropagation()}
            className="h-7 px-1.5 text-xs rounded-lg border border-slate-200 bg-white
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
            {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>

          {/* Tamaño */}
          <select
            title="Tamaño"
            onChange={applySize}
            onMouseDown={e => e.stopPropagation()}
            className="h-7 px-1.5 text-xs rounded-lg border border-slate-200 bg-white
                       text-slate-700 outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer ml-0.5">
            {SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          <Sep/>

          {/* Negrita */}
          <ToolBtn title="Negrita (Ctrl+B)" onMouseDown={e => { e.preventDefault(); exec('bold'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.75 3.75A.75.75 0 0 1 7.5 3h5.25a4.5 4.5 0 0 1 0 9H7.5a.75.75 0 0 1-.75-.75V3.75ZM7.5 10.5h5.25a3 3 0 0 0 0-6H7.5v6ZM6.75 12.75a.75.75 0 0 1 .75-.75H13.5a4.5 4.5 0 0 1 0 9H7.5a.75.75 0 0 1-.75-.75v-7.5Zm.75.75v6h5.25a3 3 0 0 0 0-6H7.5Z"/>
            </svg>
          </ToolBtn>

          {/* Cursiva */}
          <ToolBtn title="Cursiva (Ctrl+I)" onMouseDown={e => { e.preventDefault(); exec('italic'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
              <line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/>
              <line x1="15" y1="4" x2="9" y2="20"/>
            </svg>
          </ToolBtn>

          {/* Subrayado */}
          <ToolBtn title="Subrayado (Ctrl+U)" onMouseDown={e => { e.preventDefault(); exec('underline'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" y1="20" x2="20" y2="20"/>
            </svg>
          </ToolBtn>

          {/* Tachado */}
          <ToolBtn title="Tachado" onMouseDown={e => { e.preventDefault(); exec('strikeThrough'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <path d="M16 6c0-1.657-1.343-3-4-3s-4 1.343-4 3 1.5 3 4 3"/>
              <path d="M8 18c0 1.657 1.343 3 4 3s4-1.343 4-3-1.5-3-4-3"/>
            </svg>
          </ToolBtn>

          <Sep/>

          {/* Alineación izquierda */}
          <ToolBtn title="Alinear izquierda" onMouseDown={e => { e.preventDefault(); exec('justifyLeft'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/>
              <line x1="3" y1="18" x2="18" y2="18"/>
            </svg>
          </ToolBtn>

          {/* Centrado */}
          <ToolBtn title="Centrar" onMouseDown={e => { e.preventDefault(); exec('justifyCenter'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/>
              <line x1="4" y1="18" x2="20" y2="18"/>
            </svg>
          </ToolBtn>

          {/* Derecha */}
          <ToolBtn title="Alinear derecha" onMouseDown={e => { e.preventDefault(); exec('justifyRight'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/>
              <line x1="6" y1="18" x2="21" y2="18"/>
            </svg>
          </ToolBtn>

          {/* Justificado */}
          <ToolBtn title="Justificar" onMouseDown={e => { e.preventDefault(); exec('justifyFull'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </ToolBtn>

          <Sep/>

          {/* Lista viñetas */}
          <ToolBtn title="Lista con viñetas" onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/>
              <line x1="9" y1="18" x2="20" y2="18"/>
              <circle cx="4" cy="6" r="1.2" fill="currentColor"/>
              <circle cx="4" cy="12" r="1.2" fill="currentColor"/>
              <circle cx="4" cy="18" r="1.2" fill="currentColor"/>
            </svg>
          </ToolBtn>

          {/* Lista numerada */}
          <ToolBtn title="Lista numerada" onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/>
              <line x1="10" y1="18" x2="21" y2="18"/>
              <path d="M4 6h1v4" strokeLinecap="round"/><path d="M4 10h2" strokeLinecap="round"/>
              <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" strokeLinecap="round"/>
            </svg>
          </ToolBtn>

          <Sep/>

          {/* Color de texto */}
          <div className="relative">
            <ToolBtn title="Color de texto" active={open === 'color'}
              onMouseDown={e => { e.preventDefault(); toggle('color'); }}>
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <polyline points="4 7 4 4 20 4 20 7"/>
                <line x1="9" y1="20" x2="15" y2="20"/>
                <line x1="12" y1="4" x2="12" y2="20"/>
              </svg>
            </ToolBtn>
            {open === 'color' && (
              <div className="absolute top-full left-0 mt-1 z-40 bg-white rounded-xl shadow-xl
                              border border-slate-100 p-2.5 w-44">
                <p className="text-xs font-bold text-slate-400 mb-2">Color de texto</p>
                <div className="flex flex-wrap gap-1.5">
                  {TEXT_COLORS.map(c => (
                    <button key={c.value} type="button" title={c.label}
                      onMouseDown={e => { e.preventDefault(); exec('foreColor', c.value); close(); }}
                      className="w-7 h-7 rounded-lg ring-1 ring-slate-200 hover:scale-110 transition-transform"
                      style={{ background: c.value }}/>
                  ))}
                </div>
                <button type="button"
                  onMouseDown={e => { e.preventDefault(); exec('foreColor', '#1e293b'); close(); }}
                  className="w-full mt-2 text-xs text-slate-400 hover:text-slate-600 text-center">
                  Restablecer
                </button>
              </div>
            )}
          </div>

          {/* Resaltado */}
          <div className="relative">
            <ToolBtn title="Resaltar texto" active={open === 'highlight'}
              onMouseDown={e => { e.preventDefault(); toggle('highlight'); }}>
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
              </svg>
            </ToolBtn>
            {open === 'highlight' && (
              <div className="absolute top-full left-0 mt-1 z-40 bg-white rounded-xl shadow-xl
                              border border-slate-100 p-2.5 w-44">
                <p className="text-xs font-bold text-slate-400 mb-2">Resaltar texto</p>
                <div className="flex flex-wrap gap-1.5">
                  {HL_COLORS.map(c => (
                    <button key={c.value} type="button" title={c.label}
                      onMouseDown={e => {
                        e.preventDefault();
                        exec('hiliteColor', c.value);
                        close();
                      }}
                      className="w-7 h-7 rounded-lg ring-1 ring-slate-200 hover:scale-110 transition-transform"
                      style={{ background: c.value === 'transparent' ? '#fff' : c.value }}>
                      {c.value === 'transparent' && (
                        <span className="text-[9px] text-slate-400 leading-none">×</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Sep/>

          {/* Tabla */}
          <div className="relative">
            <ToolBtn title="Insertar tabla" active={open === 'table'}
              onMouseDown={e => { e.preventDefault(); toggle('table'); }}>
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
                <line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
              </svg>
            </ToolBtn>
            {open === 'table' && (
              <div className="absolute top-full left-0 mt-1 z-40 bg-white rounded-xl shadow-xl
                              border border-slate-100 p-3 w-48 space-y-2.5">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Insertar tabla</p>
                <div className="flex items-end gap-2">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs text-slate-400">Filas</label>
                    <input type="number" value={tRows} onChange={e => setTRows(e.target.value)}
                      min="1" max="20"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center
                                 outline-none focus:ring-2 focus:ring-blue-400"/>
                  </div>
                  <span className="text-slate-300 pb-2">×</span>
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs text-slate-400">Columnas</label>
                    <input type="number" value={tCols} onChange={e => setTCols(e.target.value)}
                      min="1" max="10"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center
                                 outline-none focus:ring-2 focus:ring-blue-400"/>
                  </div>
                </div>
                <button type="button"
                  onMouseDown={e => { e.preventDefault(); insertTable(); }}
                  className="w-full py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold transition-colors">
                  Insertar
                </button>
              </div>
            )}
          </div>

          <Sep/>

          {/* Limpiar formato */}
          <ToolBtn title="Limpiar formato" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}>
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 21L21 6M10 5l4 4M14 9l3 3m-9 9l4-4"/>
              <line x1="3" y1="21" x2="8" y2="21"/>
            </svg>
          </ToolBtn>
        </div>

        {/* ═══ ÁREA EDITABLE ═══ */}
        <style>{`
          .rte-editor:empty:before { content: attr(data-placeholder); color: #94a3b8; pointer-events: none; }
          .rte-editor table  { border-collapse: collapse; width: 100%; margin: 8px 0; }
          .rte-editor td, .rte-editor th { border: 1px solid #cbd5e1; padding: 6px 10px; min-width: 60px; }
          .rte-editor th     { background: #f1f5f9; font-weight: 600; }
          .rte-editor ul     { list-style: disc;    padding-left: 1.5rem; margin: 4px 0; }
          .rte-editor ol     { list-style: decimal; padding-left: 1.5rem; margin: 4px 0; }
          .rte-editor p      { margin: 2px 0; }
        `}</style>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={handleInput}
          onClick={close}
          className="rte-editor min-h-[180px] max-h-[420px] overflow-y-auto
                     px-4 py-3 text-sm text-slate-800 outline-none leading-relaxed"
        />

        {/* ═══ CONTADOR DE BYTES ═══ */}
        <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 flex items-center gap-3">
          <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300
              ${overLimit ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }}/>
          </div>
          <span className={`text-xs tabular-nums flex-shrink-0
            ${overLimit ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
            {byteLen.toLocaleString('es-PE')} / {maxBytes.toLocaleString('es-PE')} bytes
          </span>
        </div>
      </div>

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default RichTextEditor;
